import type { ReactNode } from "react";
import { useMemo } from "react";

import { useDragOperation } from "@skilitics/drag-drop";

import type { DesignerCondition } from "../../types";
import { ResponseEditorContext } from "./ResponseEditorContext";

type ResponseEditorContextProviderProps = {
  rootCondition?: DesignerCondition;
  children: ReactNode;
};

export const ResponseEditorContextProvider = ({ children, rootCondition }: ResponseEditorContextProviderProps) => {
  const { source } = useDragOperation();

  // Create a lookup of all condition ids and their descendant ids.
  const conditionDescendantsById = useMemo(() => {
    const conditionDescendants: Record<number, number[]> = {};
    if (rootCondition) {
      createConditionDescendantsById(conditionDescendants, rootCondition);
    }
    return conditionDescendants;
  }, [rootCondition]);

  // List of disabled dropzone ids - descendents of the active dragging condition group.
  const disabledDropzoneIds = useMemo(() => {
    if (!source?.id) {
      return [];
    }
    return conditionDescendantsById[Number(source.id)] ?? [];
  }, [source, conditionDescendantsById]);

  return <ResponseEditorContext.Provider value={{ disabledDropzoneIds }}>{children}</ResponseEditorContext.Provider>;
};

// This could be done a bit more efficiently in the conditionReducer, or the ResponsesDesignerContext directly accessing conditionsById in state.
const createConditionDescendantsById = (
  conditionDescendantIdsById: Record<number, number[]>,
  condition: DesignerCondition,
) =>
  (Object.values(condition.children).flat() as DesignerCondition[])
    .filter((c) => !!c)
    .flatMap((c): number[] => {
      const nestedConditionIds = createConditionDescendantsById(conditionDescendantIdsById, c);
      conditionDescendantIdsById[c.id] = [c.id, ...nestedConditionIds];
      return [c.id, ...nestedConditionIds];
    });
